import {
  type Execute,
  MAINNET_RELAY_API,
  type ProgressData,
} from '@reservoir0x/relay-sdk'
import type { Hex, WalletClient } from 'viem'
import { getRelayClient } from './client'
import type { Logger, RelayExecutionInfo } from './types'

export type RelayExecutionStep = ProgressData['currentStep'] & {}

export interface ExecuteRelayQuoteParams {
  logger: Logger
  onConfirmed: (txHash: Hex) => Promise<void>
  onError: (error: Error) => Promise<void>
  onProgress?: (step: RelayExecutionStep | null) => void
  relayQuote: Execute
  walletClient: WalletClient
}

export async function executeRelayQuote({
  logger,
  onConfirmed,
  onError,
  onProgress,
  relayQuote,
  walletClient,
}: ExecuteRelayQuoteParams) {
  await getRelayClient().actions.execute({
    quote: relayQuote,
    wallet: walletClient,
    onProgress: async ({
      steps,
      fees,
      breakdown,
      currentStep,
      currentStepItem,
      txHashes,
      details,
      error,
    }) => {
      const logPrefix = 'onRelayProgress'
      logger.info(`${logPrefix}:params`, {
        steps,
        currentStep,
        fees,
        breakdown,
        currentStepItem,
        txHashes,
        details,
        error,
      })

      onProgress?.(currentStep ?? null)

      if (error) {
        logger.info(`${logPrefix}:errorDetected`, error)
        await onError(error).catch((e) => {
          logger.error(`${logPrefix}:onErrorFailed`, e)
        })
      } else if (
        // We can redirect as soon as all the required user steps are done
        // https://fun-xyz.slack.com/archives/C08MQ85QB2N/p1748381169792379
        txHashes &&
        txHashes.length > 0 &&
        txHashes.length === steps.length
      ) {
        logger.info(`${logPrefix}:completed`, txHashes)
        // Can we assume its completed here?
        // Use first txHash as the main txHash in fun DE table
        await onConfirmed(txHashes[0].txHash as Hex).catch((e) => {
          logger.error(`${logPrefix}:onConfirmedFailed`, e)
        })
      }
    },
  })
}

export async function getRelayExecutionInfo(
  requestId: string,
): Promise<RelayExecutionInfo> {
  const url = `${MAINNET_RELAY_API}/intents/status/v2?requestId=${requestId}`
  const response = await fetch(url)
  if (!response.ok) {
    throw Error(response.statusText)
  }

  const data = await response.json()
  return data as RelayExecutionInfo
}
